import { sample } from 'effector';
import mapboxgl from 'mapbox-gl';
import booleanIntersects from '@turf/boolean-intersects';
import { combineEvents, condition, interval, spread } from 'patronum';
import dayjs from 'dayjs';
import notification from 'antd/es/notification';
import JSZip from 'jszip';
import {
  $activeLitePreset,
  $buildingInfo,
  $categoryExpanded,
  $chartFilters,
  $chatMessages,
  $chosenLiteHexagon,
  $confirmModal,
  $currentFavDetails,
  $currentFavs,
  $d11Lines,
  $d11LinesType,
  $d11Parking,
  $d11StartLines,
  $d11Stops,
  $d11Switches,
  $d9Table,
  $detailsData,
  $hexLabel,
  $isochroneTotalData,
  $isPreset,
  $krishaMarkers,
  $krishaObjects,
  $liteAggregates,
  $liteBusiness,
  $liteCategories,
  $liteErrorModal,
  $liteHexagons,
  $liteStartHexagons,
  $profileModal,
  $reportId,
  $reportLoading,
  $restoreMarker,
  $restoreModal,
  $roadsData,
  $roadsStartData,
  $showRoads,
} from './stores.js';
import {
  activatePresetEv,
  changeChartFiltersEv,
  changeChosenLiteHexEv,
  changeConfirmModalEv,
  changeCurrentFavsEv,
  changeD11LinesTypeEv,
  changeD11SwitchesEv,
  changeHexLabelEv,
  changeLiteBusinessEv,
  confirmRestoreEv,
  deleteLitePresetEv,
  downloadReportEv,
  flyToKrishaEv,
  getNewLocationEv,
  openKrishaLinkEv,
  putD11LinesEv,
  putD11ParkingEv,
  putD11StopsEv,
  putD9TableEv,
  putIsochroneTotalEv,
  putKrishaObjectsEv,
  putLiteAggregatesEv,
  putLiteHexagonsEv,
  putRoadsEv,
  renamePresetEv,
  resetChartFiltersEv,
  resetChatMessagesEv,
  resetChosenLiteHexEv,
  resetConfirmModalEv,
  resetD11SwitchesEv,
  resetRestoreMarkerEv,
  resetRestoreModalEv,
  restoreD11LinesEv,
  restoreHexagonsEv,
  restorePresetEv,
  restoreRoadsEv,
  sendCreateReportEv,
  sendPaymentUserEv,
  setActivePresetEv,
  setChatMessagesEv,
  stopCheckingReportStatusEv,
  toggleCategoryExpandedEv,
  toggleIsPresetEv,
  toggleLiteErrorModalEv,
  toggleProfileModalEv,
  toggleShowRoadsEv,
} from './events.js';
import {
  $activeFilters,
  changeActiveFilterEv,
  clearGradientEv,
} from '../activeFiltersModel/index.js';
import {
  $zoom7Hexagons,
  getZeroDataFx,
  resetChatPublicMessagesEv,
} from '../zoom7Model/index.js';
import {
  formatDetailsData,
  formatLiteHexagons,
  makeSpecKeys,
  mutateLiteHexagons,
} from './utils.js';
import { calculateThresholdsEv } from '../gradientModel/index.js';
import { flyTo, mapCenterPC } from '../../utils/mapbox-utils.js';
import {
  wsGetLiteData,
  wsSendPaymentUser,
} from '../../utils/webSocketConfig.js';
import filterHexagons from '../../counters/hex_counter/hex_counter.js';
import { $language, $userInfo } from '../authModel/index.js';
import RBP_LIte_index_help from '../../dictionaries/RBP_Lite_dict.json';
import {
  checkReportStatusFx,
  deleteLitePresetFx,
  downloadReportFx,
  downloadReportLocationFx,
  generateZipFx,
  getBuildingInfoFx,
  renamePresetFx,
  sendCreateLitePresetFx,
  sendCreateReportFx,
} from './effects.js';
import { hideLoaderEv, showLoaderEv } from '../webSocketModel/index.js';
import payment_business_types from '../../data/payment_business_types.json';
import { $tourOpen } from '../educationModel/index.js';
import { getIsochroneForPaymentsEv } from '../isochroneModel/index.js';
import { $chosenPoint, setChosenPointEv } from '../rbpLandingModel/index.js';
import {
  $activeChatHexagons,
  $chatHexagons,
  applyChatHexagonsEv,
  revertChatFiltersEv,
} from '../chatModel/index.js';
import { formatNumberCompact } from '../../utils/format-utils.js';

$isPreset.on(toggleIsPresetEv, (state, payload) => {
  if (typeof payload === 'boolean') return payload;
  return !state;
});

$profileModal.on(toggleProfileModalEv, (state, payload) => {
  if (typeof payload === 'boolean') return payload;
  return !state;
});

$chosenLiteHexagon.reset(resetChosenLiteHexEv);

$liteAggregates.on(putLiteAggregatesEv, (_, payload) => payload);

$d9Table.on(putD9TableEv, (_, payload) => payload);

$krishaObjects.on(putKrishaObjectsEv, (_, payload) =>
  payload.map((item, index) => {
    return {
      ...item,
      key: `rent_object_${index}`,
    };
  })
);

$liteBusiness.on(changeLiteBusinessEv, (_, payload) => payload);

$activeLitePreset.on(setActivePresetEv, (_, payload) => payload);

$liteErrorModal.on(toggleLiteErrorModalEv, (state, payload) => {
  if (typeof payload === 'boolean') return payload;
  return !state;
});

$categoryExpanded.on(toggleCategoryExpandedEv, (state, payload) => {
  if (typeof payload === 'boolean') return payload;
  return !state;
});

$chartFilters.reset(resetChartFiltersEv);

$buildingInfo.on(setActivePresetEv, (state, payload) => payload.building_info);

$chatMessages
  .on(setChatMessagesEv, (state, payload) => payload)
  .reset(resetChatMessagesEv);

$confirmModal
  .on(changeConfirmModalEv, (state, payload) => payload)
  .reset(resetConfirmModalEv);

$restoreModal.reset(resetRestoreModalEv);

$d11LinesType.on(changeD11LinesTypeEv, (state, payload) => {
  if (typeof payload === 'boolean') return payload;
  return !state;
});

$d11Switches.reset(resetD11SwitchesEv);

$reportId.reset(stopCheckingReportStatusEv);

$reportLoading
  .on(sendCreateReportFx.doneData, (_, payload) => true)
  .reset(stopCheckingReportStatusEv);

sample({
  source: [
    $isochroneTotalData,
    $chosenLiteHexagon,
    $d9Table,
    $isPreset,
    $liteAggregates,
    $language,
  ],
  clock: $activeFilters.updates,
  filter: ([_, __, ___, isPreset]) =>
    isPreset && window.location.pathname === '/lite',
  fn: (
    [total, chosenHex, d9Table, _, aggregates, language],
    { chosen_metrics }
  ) => {
    const source = chosenHex ? chosenHex.properties : total;
    const d9Source = chosenHex
      ? d9Table.isochrone.find(
          (item) => chosenHex.properties.zoom9_id === item.zoom_id
        ).data
      : d9Table.city.data;
    const aggregatesSource = chosenHex ? aggregates.isochrone : aggregates.city;
    return formatDetailsData(
      source,
      chosen_metrics,
      d9Source,
      aggregatesSource,
      language
    );
  },
  target: $detailsData,
});

sample({
  source: [
    $liteStartHexagons,
    $isPreset,
    $liteAggregates,
    $showRoads,
    $chartFilters,
    $hexLabel,
  ],
  clock: $activeFilters.updates,
  filter: ([_, isPreset, __, showRoads]) =>
    isPreset && window.location.pathname === '/lite' && !showRoads,
  fn: ([source, _, aggregates, __, chartFilters, hexLabel], activeFilters) => {
    let hexByCategory = mutateLiteHexagons(
      source,
      activeFilters.chosen_metrics[0],
      makeSpecKeys(chartFilters),
      aggregates.isochrone,
      hexLabel
    );
    if (activeFilters.gradient.length > 0) {
      hexByCategory = filterHexagons(hexByCategory, activeFilters, [], 'index');
    }
    return hexByCategory;
  },
  target: [$liteHexagons, calculateThresholdsEv],
});

sample({
  source: $currentFavs,
  clock: changeCurrentFavsEv,
  fn: (source, clock) => {
    if (source.includes(clock)) {
      return source.filter((item) => item !== clock);
    }
    return [...source, clock];
  },
  target: $currentFavs,
});

sample({
  source: [
    $isochroneTotalData,
    $chosenLiteHexagon,
    $d9Table,
    $liteAggregates,
    $language,
  ],
  clock: $currentFavs.updates,
  fn: ([total, chosenHex, d9Table, aggregates, language], clock) => {
    const source = chosenHex ? chosenHex.properties : total;
    const d9Source = chosenHex
      ? d9Table.isochrone.find(
          (item) => chosenHex.properties.zoom9_id === item.zoom_id
        ).data
      : d9Table.city.data;
    const aggregatesSource = chosenHex ? aggregates.isochrone : aggregates.city;
    return formatDetailsData(
      source,
      clock,
      d9Source,
      aggregatesSource,
      language
    );
  },
  target: $currentFavDetails,
});

sample({
  source: $chartFilters,
  clock: changeChartFiltersEv,
  fn: (source, clock) => {
    if (
      source.some((item) => {
        return Object.keys(item).every((field) => item[field] === clock[field]);
      })
    ) {
      return source.filter((item) => {
        return !Object.keys(item).every(
          (field) => item[field] === clock[field]
        );
      });
    }
    return [...source, clock];
  },
  target: [$chartFilters, clearGradientEv],
});

sample({
  clock: changeActiveFilterEv,
  filter: (clock) =>
    window.location.pathname === '/lite' && clock.field === 'chosen_metrics',
  fn: () => [],
  target: $chartFilters,
});

const combinePutHexAgg = combineEvents({
  events: {
    hexagons: putLiteHexagonsEv,
    aggregates: putLiteAggregatesEv,
  },
});

sample({
  clock: combinePutHexAgg,
  fn: (clock) => {
    return formatLiteHexagons(clock.hexagons, clock.aggregates);
  },
  target: [$liteHexagons, $liteStartHexagons, calculateThresholdsEv],
});

sample({
  source: $liteHexagons,
  clock: $isPreset.updates,
  filter: (source, clock) => clock,
  fn: (source) => source,
  target: calculateThresholdsEv,
});

sample({
  clock: putIsochroneTotalEv,
  fn: (clock) => {
    return {
      ...clock,
      d11i1: +(clock.d11i1 * 10).toFixed(1),
      d11i2: +(clock.d11i2 * 10).toFixed(1),
      d11i3: +(clock.d11i3 * 10).toFixed(1),
      d11i4: +(clock.d11i4 * 10).toFixed(1),
    };
  },
  target: $isochroneTotalData,
});

sample({
  source: [$activeFilters, $language],
  clock: putIsochroneTotalEv,
  fn: ([activeFilters, language], clock) =>
    formatDetailsData(
      clock,
      activeFilters.chosen_metrics,
      undefined,
      undefined,
      language
    ),
  target: $detailsData,
});

sample({
  source: $chosenLiteHexagon,
  clock: changeChosenLiteHexEv,
  fn: (source, clock) => {
    if (source && source.id === clock.id) {
      return null;
    }
    return clock;
  },
  target: $chosenLiteHexagon,
});

sample({
  source: [
    $isochroneTotalData,
    $activeFilters,
    $d9Table,
    $liteAggregates,
    $language,
  ],
  clock: $chosenLiteHexagon.updates,
  fn: ([total, activeFilters, d9Table, aggregates, language], clock) => {
    const source = clock ? clock.properties : total;
    const d9Source = clock
      ? d9Table.isochrone.find(
          (item) => clock.properties.zoom9_id === item.zoom_id
        ).data
      : d9Table.city.data;
    const aggregatesSource = clock ? aggregates.isochrone : aggregates.city;
    return formatDetailsData(
      source,
      activeFilters.chosen_metrics,
      d9Source,
      aggregatesSource,
      language
    );
  },
  target: $detailsData,
});

sample({
  source: [
    $isochroneTotalData,
    $chosenLiteHexagon,
    $currentFavs,
    $d9Table,
    $liteAggregates,
    $language,
  ],
  clock: $chosenLiteHexagon.updates,
  fn: ([total, chosenHex, favs, d9Table, aggregates, language], clock) => {
    const source = chosenHex ? chosenHex.properties : total;
    const d9Source = chosenHex
      ? d9Table.isochrone.find(
          (item) => chosenHex.properties.zoom9_id === item.zoom_id
        ).data
      : d9Table.city.data;
    const aggregatesSource = chosenHex ? aggregates.isochrone : aggregates.city;
    return formatDetailsData(
      source,
      favs,
      d9Source,
      aggregatesSource,
      language
    );
  },
  target: $currentFavDetails,
});

sample({
  source: $liteCategories,
  clock: putLiteAggregatesEv,
  fn: (source, clock) => {
    return source.map((item) => {
      let value = Math.round(clock.city[item.index_name].current);
      let avg = Math.round(clock.city[item.index_name].mean);
      let total = Math.round(clock.city[item.index_name].max);
      let sub_value;
      let sub_avg;

      if (item.index_name === 'd11') {
        value = Math.round(clock.city[item.index_name].current * 10);
        avg = Math.round(clock.city[item.index_name].mean * 10);
        total = 10;
      }

      if (item.index_name === 'd10') {
        value = clock.city.d10p.current;
        avg = clock.city.d10p.mean;
        total = clock.city.d10p.max;
        sub_value = Math.round(clock.city.d10p.current / 1000);
        sub_avg = Math.round(clock.city.d10p.mean / 1000);
      }

      if (item.index_name === 'd7') {
        value = Math.round(clock.city.d7t.current);
        avg = Math.round(clock.city.d7t.mean);
        total = Math.round(clock.city.d7t.max);
      }

      return {
        ...item,
        value,
        avg,
        total,
        sub_value,
        sub_avg,
      };
    });
  },
  target: $liteCategories,
});

sample({
  source: [$liteAggregates, $liteCategories],
  clock: $chosenLiteHexagon.updates,
  fn: ([aggregates, categories], chosen) => {
    const aggSource = chosen ? aggregates.isochrone : aggregates.city;

    return categories.map((item) => {
      let value = chosen
        ? Math.round(
            aggSource[item.index_name].current[chosen.properties.zoom9_id]
          )
        : Math.round(aggSource[item.index_name].current);
      let avg = Math.round(aggSource[item.index_name].mean);
      let total = Math.round(aggSource[item.index_name].max);

      let sub_value;
      let sub_avg;

      if (item.index_name === 'd11') {
        value = chosen
          ? Math.round(
              aggSource[item.index_name].current[chosen.properties.zoom9_id] *
                10
            )
          : Math.round(aggSource[item.index_name].current) * 10;
        avg = Math.round(aggSource[item.index_name].mean * 10);
        total = 10;
      }

      if (item.index_name === 'd10') {
        value = chosen
          ? aggSource.d10p.current[chosen.properties.zoom9_id]
          : aggSource.d10p.current;
        avg = aggSource.d10p.mean;
        total = aggSource.d10p.max;
        sub_value = chosen
          ? Math.round(
              aggSource.d10p.current[chosen.properties.zoom9_id] / 1000
            )
          : Math.round(aggSource.d10p.current / 1000);
        sub_avg = Math.round(aggSource.d10p.mean / 1000);
      }

      return {
        ...item,
        value,
        avg,
        total,
        sub_value,
        sub_avg,
      };
    });
  },
  target: $liteCategories,
});

sample({
  source: [
    $liteHexagons,
    $activeFilters,
    $isPreset,
    $hexLabel,
    $liteAggregates,
  ],
  clock: $chartFilters.updates,
  filter: ([_, __, isPreset]) => isPreset,
  fn: ([hexagons, { chosen_metrics }, _, hexLabel, aggregates], clock) => {
    return mutateLiteHexagons(
      hexagons,
      chosen_metrics[0],
      makeSpecKeys(clock),
      aggregates.isochrone,
      hexLabel
    );
  },
  target: [$liteHexagons, calculateThresholdsEv],
});

sample({
  source: $krishaMarkers,
  clock: flyToKrishaEv,
  fn: (source, clock) => {
    if (
      source &&
      source._lngLat.lng === clock.coords[0] &&
      source._lngLat.lat === clock.coords[1] &&
      source._element.className.includes(`marker_${clock.id}`)
    ) {
      source.remove();
      flyTo(clock.coords, 11);
      return null;
    }
    if (source) {
      source.remove();
    }
    flyTo(clock.coords, 15);
    return new mapboxgl.Marker({
      test: 'test',
    })
      .setLngLat(clock.coords)
      .addClassName(`marker_${clock.id}`)
      .addTo(window.map);
  },
  target: $krishaMarkers,
});

sample({
  clock: $isPreset.updates,
  fn: (isPreset) => {
    if (isPreset) {
      return {
        field: 'chosen_metrics',
        value: 'd1',
      };
    }
    return {
      field: 'chosen_metrics',
      value: `competitors_cnt_B`,
    };
  },
  target: changeActiveFilterEv,
});

sample({
  source: [$zoom7Hexagons, $liteBusiness, $userInfo, $tourOpen],
  clock: getNewLocationEv,
  filter: ([_, __, userInfo, tourOpen]) =>
    userInfo.presets_all !== userInfo.presets_used && !tourOpen,
  fn: ([hexagons, liteBusiness], point) => {
    const uuid = `t${liteBusiness.business}pt${liteBusiness.sub_business}`;
    const category = payment_business_types.find((type) => {
      return type.subcategories.find((elem) => elem.uuid === uuid);
    });

    if (window.marker) {
      window.marker.remove();
    }
    if (window.popup) {
      window.popup.remove();
    }

    return {
      lon: point.geometry.coordinates[0],
      lat: point.geometry.coordinates[1],
      ...liteBusiness,
      name: `${category.type}, ${
        category.subcategories.find((subtype) => subtype.uuid === uuid).subtype
      }_${dayjs().format('YYYY-MM-DD HH:mm')}`,
    };
  },
  target: [sendCreateLitePresetFx, showLoaderEv],
});

sample({
  source: $userInfo,
  clock: getNewLocationEv,
  filter: (userInfo) => userInfo.presets_all === userInfo.presets_used,
  fn: () => true,
  target: toggleProfileModalEv,
});

sample({
  source: $userInfo,
  clock: sendCreateLitePresetFx.doneData,
  filter: (source, clock) => Object.keys(clock).length > 0,
  fn: (source, clock) => {
    return {
      userInfo: {
        ...source,
        presets_used: source.presets_used + 1,
        presets: {
          ...source.presets,
          rbp_lite: [
            ...(Object.hasOwn(clock, 'old_id')
              ? source.presets.rbp_lite.filter(
                  (item) => item.id !== clock.old_id
                )
              : source.presets.rbp_lite),
            {
              id: clock.id,
              ...clock.data,
            },
          ],
        },
      },
      activatePreset: clock.data.isochrones,
      isPreset: true,
      createReport: clock.id,
    };
  },
  target: spread({
    targets: {
      userInfo: $userInfo,
      activatePreset: [
        activatePresetEv,
        hideLoaderEv,
        resetChatMessagesEv,
        resetChatPublicMessagesEv,
        resetConfirmModalEv,
        resetRestoreModalEv,
        revertChatFiltersEv,
      ],
      isPreset: toggleIsPresetEv,
      createReport: sendCreateReportEv,
    },
  }),
});

sample({
  clock: sendCreateLitePresetFx.doneData,
  filter: (clock) => Object.keys(clock).length === 0,
  fn: () => true,
  target: [toggleLiteErrorModalEv, hideLoaderEv, resetConfirmModalEv],
});

sample({
  clock: activatePresetEv,
  fn: (clock) => {
    wsGetLiteData(clock);
    flyTo([clock.lon, clock.lat], window.map.getZoom());
    return {
      lat: clock.lat,
      lon: clock.lon,
    };
  },
  target: [setChosenPointEv],
});

sample({
  clock: $activeLitePreset.updates,
  fn: (clock) => {
    return {
      business: clock.business,
      sub_business: clock.sub_business,
    };
  },
  target: changeLiteBusinessEv,
});

sample({
  clock: putRoadsEv,
  fn: (clock) => {
    return clock.map((road) => {
      return {
        ...road,
        properties: {
          ...road.properties,
          index_main: road.properties.d5_lines,
        },
      };
    });
  },
  target: [$roadsData, $roadsStartData],
});

sample({
  source: $showRoads,
  clock: toggleShowRoadsEv,
  fn: (source, clock) => {
    if (typeof clock === 'boolean') return clock;
    return !source;
  },
  target: $showRoads,
});

condition({
  source: $showRoads,
  if: (showRoads) => showRoads,
  then: restoreRoadsEv,
  else: restoreHexagonsEv,
});

sample({
  source: [$liteStartHexagons, $liteAggregates, $hexLabel, $activeFilters],
  clock: restoreHexagonsEv,
  fn: ([hexagons, aggregates, hexLabel, activeFilters]) => {
    return mutateLiteHexagons(
      hexagons,
      activeFilters.chosen_metrics[0],
      [],
      aggregates.isochrone,
      hexLabel
    );
  },
  target: [$liteHexagons, calculateThresholdsEv],
});

sample({
  source: $roadsStartData,
  clock: restoreRoadsEv,
  fn: (source) => source,
  target: [$roadsData, calculateThresholdsEv],
});

sample({
  source: [$roadsStartData, $showRoads],
  clock: $activeFilters.updates,
  filter: ([_, showRoads]) => showRoads,
  fn: ([roads, _], activeFilters) => {
    return filterHexagons(roads, activeFilters, [], 'index');
  },
  target: $roadsData,
});

sample({
  source: [$detailsData, $liteCategories],
  clock: $language.updates,
  fn: ([details, categories], language) => {
    return {
      details: details.map((detail) => {
        return {
          ...detail,
          info: RBP_LIte_index_help[detail.metric.replace('d', '')][
            `description_${language}`
          ],
          title:
            RBP_LIte_index_help[detail.metric.replace('d', '')][
              `name_${language}`
            ],
        };
      }),
      categories: categories.map((category) => {
        return {
          ...category,
          info: RBP_LIte_index_help[category.index_name.replace('d', '')][
            `description_${language}`
          ],
          title:
            RBP_LIte_index_help[category.index_name.replace('d', '')][
              `name_${language}`
            ],
        };
      }),
    };
  },
  target: spread({
    targets: {
      details: $detailsData,
      categories: $liteCategories,
    },
  }),
});

sample({
  clock: downloadReportEv,
  target: [downloadReportFx, downloadReportLocationFx],
});

const gotReportsEv = combineEvents({
  events: {
    excel: downloadReportFx.doneData,
    pdf: downloadReportLocationFx.doneData,
  },
});

sample({
  clock: gotReportsEv,
  target: generateZipFx,
});

sample({
  clock: renamePresetEv,
  fn: (clock) => {
    return {
      id: clock.id,
      data: {
        name: clock.value,
      },
    };
  },
  target: renamePresetFx,
});

sample({
  source: $userInfo,
  clock: renamePresetFx.doneData,
  fn: (source, clock) => {
    const current_preset = source.presets.rbp_lite.find(
      (preset) => preset.id === clock.id
    );
    const presetIndex = source.presets.rbp_lite.findIndex(
      (preset) => preset.id === clock.id
    );

    return {
      ...source,
      presets: {
        ...source.presets,
        rbp_lite: [
          ...source.presets.rbp_lite.slice(0, presetIndex),
          {
            ...current_preset,
            name: clock.data.name,
          },
          ...source.presets.rbp_lite.slice(presetIndex + 1),
        ],
      },
    };
  },
  target: $userInfo,
});

sample({
  clock: deleteLitePresetEv,
  target: deleteLitePresetFx,
});

sample({
  source: $userInfo,
  clock: deleteLitePresetFx.doneData,
  fn: (source, clock) => {
    return {
      ...source,
      presets_used: source.presets_used - 1,
      presets: {
        ...source.presets,
        rbp_lite: source.presets.rbp_lite.filter(
          (preset) => preset.id !== clock
        ),
      },
    };
  },
  target: $userInfo,
});

sample({
  clock: sendPaymentUserEv,
  fn: (clock) => {
    wsSendPaymentUser({
      // product: clock,
      product: 'location_test',
    });
  },
});

sample({
  source: $activeLitePreset,
  clock: openKrishaLinkEv,
  fn: (source) => window.open(source.krisha_url, '_blank'),
});

sample({
  source: $chosenPoint,
  clock: $isPreset,
  filter: (source) => source,
  fn: (source, clock) => {
    if (clock) {
      source.addTo(window.map);
    } else {
      source.remove();
    }
  },
});

sample({
  source: $hexLabel,
  clock: changeHexLabelEv,
  fn: (source, clock) => {
    if (Array.isArray(clock)) {
      return clock.filter((item) => !source.includes(item));
    }
    return [clock];
  },
  target: $hexLabel,
});

sample({
  source: [$liteHexagons, $liteAggregates],
  clock: $hexLabel.updates,
  fn: ([source, aggregates], clock) => {
    const regex = new RegExp(`${clock}[a-z]`, 'g');
    const category_keys = Object.keys(source[0].properties)
      .filter((item) => item.match(regex))
      .filter((item) => !item.includes('ttl'));
    return source.map((item) => {
      // let label = category_keys.reduce((a, b) => a + +item.properties[b], 0);
      // if (clock[0] === 'd9') {
      //   let divider = 0;
      //   category_keys.forEach((key) => {
      //     if (item.properties[key] !== null) divider += 1;
      //   });
      //   label = Math.round(label / divider);
      // }

      let label = aggregates.isochrone[clock].current[item.properties.zoom9_id];
      if (clock[0] === 'd9') {
        let divider = 0;
        category_keys.forEach((key) => {
          if (item.properties[key] !== null) divider += 1;
        });
        label = Math.round(label / divider);
      }
      if (clock[0] === 'd10') {
        label = formatNumberCompact(item.properties.d10p_max);
      }
      if (clock[0] === 'd3' || clock[0] === 'd4') {
        label = Math.round(label / 1000);
      }
      if (clock[0] === 'd11') {
        label = `${label * 10}/10`;
      }

      return {
        ...item,
        properties: {
          ...item.properties,
          label,
        },
      };
    });
  },
  target: $liteHexagons,
});

sample({
  source: $userInfo,
  clock: restorePresetEv,
  filter: (source, _) => source.presets_all !== source.presets_used,
  fn: (userInfo, clock) => {
    const payload = {
      lon: clock.isochrones.lon,
      lat: clock.isochrones.lat,
      business: clock.isochrones.business,
      sub_business: clock.isochrones.sub_business,
      name: `${clock.name}_restored`,
      id: clock.id,
    };
    flyTo([clock.isochrones.lon, clock.isochrones.lat], 13);
    const business_payload = {
      business: clock.isochrones.business,
      sub_business: clock.isochrones.sub_business,
    };
    const chosenPoint = {
      lon: clock.isochrones.lon,
      lat: clock.isochrones.lat,
    };

    const restoreMarker = new mapboxgl.Marker({ scale: 0.5 })
      .setLngLat([clock.isochrones.lon, clock.isochrones.lat])
      .addTo(window.map);

    return {
      isPreset: false,
      restoreModal: payload,
      business: business_payload,
      restoreMarker,
    };
  },
  target: spread({
    targets: {
      isPreset: toggleIsPresetEv,
      restoreModal: $restoreModal,
      business: changeLiteBusinessEv,
      restoreMarker: $restoreMarker,
    },
  }),
});

// sample({
//   source: [
//     $liteStartHexagons,
//     $chatHexagons,
//     $activeFilters,
//     $chartFilters,
//     $hexLabel,
//     $liteAggregates,
//   ],
//   clock: applyChatHexagonsEv,
//   filter: () => window.location.pathname === '/lite',
//   fn: (
//     [
//       startData,
//       chatHexagons,
//       activeFilters,
//       chartFilters,
//       hexLabel,
//       aggregates,
//     ],
//     activeMessage
//   ) => {
//     return mutateLiteHexagons(
//       startData.filter((item) => {
//         return chatHexagons[activeMessage].includes(item.properties.zoom9_id);
//       }),
//       activeFilters.chosen_metrics[0],
//       makeSpecKeys(chartFilters),
//       aggregates.isochrone,
//       hexLabel
//     );
//   },
//   target: $liteHexagons,
// });

sample({
  source: $chatHexagons,
  clock: applyChatHexagonsEv,
  filter: () => window.location.pathname === '/lite',
  fn: (source, activeMessage) => {
    flyTo(mapCenterPC, 11);
    return source[activeMessage];
  },
  target: $activeChatHexagons,
});

// sample({
//   source: [
//     $liteStartHexagons,
//     $activeFilters,
//     $chartFilters,
//     $hexLabel,
//     $liteAggregates,
//   ],
//   clock: revertChatFiltersEv,
//   filter: () => window.location.pathname === '/lite',
//   fn: ([startData, activeFilters, chartFilters, hexLabel, aggregates], _) => {
//     return mutateLiteHexagons(
//       startData,
//       activeFilters.chosen_metrics[0],
//       makeSpecKeys(chartFilters),
//       aggregates.isochrone,
//       hexLabel
//     );
//   },
//   target: $liteHexagons,
// });

sample({
  clock: $language.updates,
  fn: (clock) => {
    return [
      {
        text:
          clock === 'ru'
            ? `Добро пожаловать!<br/>
      Я ваш виртуальный помощник по оценке жизнестойкости города.
Я помогу вам ориентироваться в методологии, анализировать данные и работать с интерактивной картой.`
            : `Welcome!<br/>
      I am your virtual assistant for assessing urban resilience.
I will help you navigate the methodology, analyze data, and work with the interactive map.`,
        fromUser: false,
      },
    ];
  },
  target: $chatMessages,
});

sample({
  source: $d11Switches,
  clock: changeD11SwitchesEv,
  fn: (source, clock) => {
    return {
      ...source,
      [clock.field]: clock.value,
    };
  },
  target: $d11Switches,
});

sample({
  source: $restoreModal,
  clock: confirmRestoreEv,
  fn: (source) => source,
  target: sendCreateLitePresetFx,
});

condition({
  source: $d11Switches,
  if: (d11Switches) => d11Switches.traffic_jams,
  then: restoreD11LinesEv,
  else: restoreHexagonsEv,
});

sample({
  source: $d11Lines,
  clock: restoreD11LinesEv,
  fn: (source, clock) => source,
  target: calculateThresholdsEv,
});

sample({
  source: $d11StartLines,
  clock: $d11LinesType.updates,
  fn: (source, clock) => {
    if (clock) {
      return source.pm;
    }
    return source.am;
  },
  target: [$d11Lines, calculateThresholdsEv],
});

sample({
  clock: putD11ParkingEv,
  fn: (clock) => {
    return clock.map((item) => {
      return {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [item.lon, item.lat],
        },
        properties: {
          ...item,
        },
      };
    });
  },
  target: $d11Parking,
});

sample({
  clock: putD11StopsEv,
  fn: (clock) => {
    return clock.map((item) => {
      return {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [item.lon, item.lat],
        },
        properties: {
          ...item,
        },
      };
    });
  },
  target: $d11Stops,
});

sample({
  clock: putD11LinesEv,
  fn: (clock) => {
    const start = {
      am: clock.am.map((item) => {
        return {
          ...item,
          properties: {
            ...item.properties,
            index_main: item.properties.tj,
          },
        };
      }),
      pm: clock.pm.map((item) => {
        return {
          ...item,
          properties: {
            ...item.properties,
            index_main: item.properties.tj,
          },
        };
      }),
    };
    const lines = [...start.am];

    return {
      start,
      lines,
    };
  },
  target: spread({
    targets: {
      start: $d11StartLines,
      lines: $d11Lines,
    },
  }),
});

sample({
  source: $restoreMarker,
  clock: resetRestoreMarkerEv,
  fn: (marker) => {
    marker.remove();
    return null;
  },
  target: $restoreMarker,
});

sample({
  clock: sendCreateReportEv,
  fn: (clock) => {
    return {
      preset_id: clock,
    };
  },
  target: sendCreateReportFx,
});

sample({
  source: $userInfo,
  clock: sendCreateReportFx.doneData,
  fn: (source, clock) => {
    const chosen_preset = source.presets.rbp_lite.find(
      (item) => item.id === clock.preset_id
    );
    const chosen_index = source.presets.rbp_lite.findIndex(
      (item) => item.id === clock.preset_id
    );
    return {
      userInfo: {
        ...source,
        presets: {
          ...source.presets,
          rbp_lite: [
            ...source.presets.rbp_lite.slice(0, chosen_index),
            {
              ...chosen_preset,
              reports_location_id: clock.id,
            },
            ...source.presets.rbp_lite.slice(chosen_index + 1),
          ],
        },
      },
      report_id: clock.id,
    };
  },
  target: spread({
    targets: {
      userInfo: $userInfo,
      report_id: $reportId,
    },
  }),
});

const checkReportStatusEv = interval({
  timeout: 30000,
  leading: true,
  start: sendCreateReportFx.doneData,
  stop: stopCheckingReportStatusEv,
});

sample({
  source: $reportId,
  clock: checkReportStatusEv.tick,
  fn: (source) => source,
  target: checkReportStatusFx,
});

condition({
  source: checkReportStatusFx.doneData,
  if: (data) => data.status === 'ready',
  then: stopCheckingReportStatusEv,
});

condition({
  source: checkReportStatusFx.doneData,
  if: (data) => data.status === 'failed',
  then: stopCheckingReportStatusEv,
});
